import { isSitefinityEditor } from './helpers.js';
import { header } from './global.js';

const body = document.body;

/****************************************************************************************************/
/* #region Sticky Header */
/****************************************************************************************************/

function initStickyHeader() {
    if (!header || isSitefinityEditor()) return;

    // Sentinel sits above #site-header so the observer fires the moment
    // the header sticks to the top of the viewport.
    const sentinel = document.createElement('div');
    sentinel.className = 'sticky-sentinel';
    sentinel.setAttribute('aria-hidden', 'true');
    header.parentNode.insertBefore(sentinel, header);

    // IntersectionObserver: is-stuck when sentinel leaves viewport
    const observer = new IntersectionObserver(
        ([entry]) => {
            header.classList.toggle('is-stuck', !entry.isIntersecting);
        },
        { threshold: 0 }
    );
    observer.observe(sentinel);

    // ResizeObserver: keep --header-height CSS var in sync
    const resizeObserver = new ResizeObserver(() => {
        const h = header.getBoundingClientRect().height;
        document.documentElement.style.setProperty('--header-height', `${h}px`);
        window.hh = h;
    });
    resizeObserver.observe(header);
}

/* #endregion */
/****************************************************************************************************/

/****************************************************************************************************/
/* #region Header Scrub */
/****************************************************************************************************/

// Holds the active scroll handler so it can be removed before re-registering
// on resize (prevents stacking duplicate listeners).
let _headerScrubHandler = null;

function initHeaderScrub() {
    if (!header || isSitefinityEditor()) return;

    const logoImg = header.querySelector('.logo img');
    const links   = header.querySelector('.links');

    // Remove any handler from a previous init (e.g. after a resize).
    if (_headerScrubHandler) {
        window.removeEventListener('scroll', _headerScrubHandler, { passive: true });
        _headerScrubHandler = null;
    }

    // Clear inline styles so we measure the true CSS natural values.
    header.style.marginTop = '';
    if (logoImg) logoImg.style.width = '';
    if (links)   links.style.transition = 'none';

    // Capture natural values now, after clearing GSAP/inline overrides.
    const logoNaturalWidth = logoImg ? logoImg.getBoundingClientRect().width : 0;
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize);
    const logoTargetWidth  = 6 * rem;   // compact width when fully scrolled
    const headerMaxMargin  = -5 * rem;  // max upward shift when fully scrolled

    function update() {
        const sy = window.scrollY;

        // Header + logo: scrub from scroll 0 → 300px
        const scrubP = Math.min(1, Math.max(0, sy / 300));
        header.style.marginTop = (headerMaxMargin * scrubP) + 'px';
        if (logoImg) {
            logoImg.style.width = (logoNaturalWidth + (logoTargetWidth - logoNaturalWidth) * scrubP) + 'px';
        }

        // Links: fade from scroll 0 → 100px
        if (links) {
            const fadeP = Math.min(1, Math.max(0, sy / 100));
            links.style.opacity    = String(1 - fadeP);
            links.style.visibility = fadeP >= 1 ? 'hidden' : 'inherit';
        }
    }

    _headerScrubHandler = update;
    window.addEventListener('scroll', update, { passive: true });
    update(); // Apply correct state for the current scroll position immediately.
}

/* #endregion */
/****************************************************************************************************/

/****************************************************************************************************/
/* #region Stuck Fade */
/****************************************************************************************************/

// Fades out each .stuck-fade element as it approaches the sticky header.
// start/end are functions so ScrollTrigger re-evaluates them on every refresh
// (resize), keeping the window valid at any viewport size.
// header.getBoundingClientRect().bottom is used (rather than window.hh) so the
// reference reflects the header's true viewport bottom position, not just its
// height.  The header also shrinks via marginTop as the user scrolls, so this
// approach anchors the fade zone to where the header bottom actually is at
// scroll=0 / resize time.
function initStuckFade() {
    if (isSitefinityEditor()) return;

    gsap.registerPlugin(ScrollTrigger);

    const getHeaderBottom = () =>
        header ? header.getBoundingClientRect().bottom : (window.hh || 100);

    document.querySelectorAll('.stuck-fade').forEach((el) => {
        // CSS transitions fight the scrub: GSAP sets opacity on every scroll tick
        // but `transition: all` delays the visual update by its duration, causing
        // the fade to lag behind the scroll position and appear to finish late.
        gsap.set(el, { clearProps: 'transition' });
        el.style.transition = 'none';

        gsap.to(el, {
            autoAlpha: 0,
            ease: 'none',
            scrollTrigger: {
                trigger: el,
                // Clamp start so the trigger's scroll position is never negative.
                // If the element naturally sits inside the fade zone at scroll=0
                // (e.g. a hero h1 close to the header), an unclamped start would
                // produce trigger.start < 0, which GSAP resolves as progress > 0
                // at scrollY=0 — causing the element to appear partially faded on
                // load/resize without the user having scrolled at all.
                start: () => {
                    const hb      = getHeaderBottom();
                    const docTop  = el.getBoundingClientRect().top + window.scrollY;
                    return `top ${Math.min(hb + 120, docTop)}px`;
                },
                // Keep the fade window at a fixed 80 px regardless of clamping.
                end: () => {
                    const hb      = getHeaderBottom();
                    const docTop  = el.getBoundingClientRect().top + window.scrollY;
                    const startPx = Math.min(hb + 120, docTop);
                    return `top ${startPx - 80}px`;
                },
                scrub: 0,
                invalidateOnRefresh: true,
            },
        });
    });
}

/* #endregion */
/****************************************************************************************************/

/****************************************************************************************************/
/* #region Scroll Reveal */
/****************************************************************************************************/

// Selectors whose elements should fade in the first time they enter the viewport.
// Add or remove entries here to control which elements get the reveal treatment.
const SCROLL_REVEAL_SELECTORS = [
    '.scroll-reveal',
    '.prose',
    ':is(h1,h2,h3,h4,h5,h6)',
];

function initScrollReveal() {
    if (isSitefinityEditor()) return;

    const selector = SCROLL_REVEAL_SELECTORS.join(', ');
    const elements = document.querySelectorAll(selector);
    if (!elements.length) return;

    const observer = new IntersectionObserver(
        (entries, obs) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                entry.target.classList.add('is-revealed');
                obs.unobserve(entry.target); // fire once only
            });
        },
        {
            threshold: 0.1,      // trigger when 10% of the element is visible
            rootMargin: '0px 0px -40px 0px', // slight bottom offset for a natural feel
        }
    );

    elements.forEach((el) => {
        // Skip anything already in view on load (above the fold)
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
            el.classList.add('is-revealed');
        } else {
            observer.observe(el);
        }
    });
}

/* #endregion */
/****************************************************************************************************/

/****************************************************************************************************/
/* #region Home Team Border Radius */
/****************************************************************************************************/

// Smoothly collapses the border-radius of .home-team-section to 0 as its top
// edge approaches the viewport top, then restores it as the bottom edge leaves.
// Each transition spans 200px of scroll travel.

/*
function initHomeTeamBorderRadius() {
    if (isSitefinityEditor()) return;

    const section = document.querySelector('.home-team-section');
    if (!section) return;

    gsap.registerPlugin(ScrollTrigger);

    // Snapshot the CSS-defined radius before GSAP writes any inline style
    const startRadius = getComputedStyle(section).borderRadius;

    // Top edge: section top scrolls from 200px below viewport top to flush with it
    gsap.fromTo(section,
        { borderRadius: startRadius },
        {
            borderRadius: 0,
            ease: 'none',
            scrollTrigger: {
                trigger: section,
                start: 'top 200px',
                end: 'top top',
                scrub: true,
            }
        }
    );

    // Bottom edge: section bottom scrolls from 200px below viewport top to flush with it
    gsap.fromTo(section,
        { borderRadius: 0 },
        {
            borderRadius: startRadius,
            ease: 'none',
            scrollTrigger: {
                trigger: section,
                start: 'bottom 200px',
                end: 'bottom top',
                scrub: true,
            }
        }
    );
}

*/

/* #endregion */
/****************************************************************************************************/

/****************************************************************************************************/
/* #region Init */
/****************************************************************************************************/

document.addEventListener('DOMContentLoaded', () => {
    initStickyHeader();
    initStuckFade();
    initScrollReveal();
    initHeaderScrub();
});

/* #endregion */
/****************************************************************************************************/
